import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Priority } from "../lib/design";
import { seedViews, type SavedView } from "../lib/viewEngine";

/** Toggles de notification par type d'event (DT F-06). Les trois premiers
 * filtrent la dérivation locale ; les autres s'appliqueront à l'inbox du
 * Notification Engine IC quand elle existera. */
export interface NotifPrefs {
  overdue: boolean;
  due_today: boolean;
  due_soon: boolean;
  assigned: boolean;
  mention: boolean;
  recurrence: boolean;
}

/** Préférences utilisateur — localStorage.
 * ponytail: pas d'endpoint prefs backend ; endpoint TM si multi-device requis. */
export interface Prefs {
  defaultListId: string | null;
  defaultVisibility: "private" | "team" | "tenant";
  defaultPriority: Priority;
  theme: "light" | "dark" | "auto";
  lang: "fr";
  weekStart: "mon" | "sun";
  notifs: NotifPrefs;
}

export const DEFAULT_NOTIFS: NotifPrefs = {
  overdue: true,
  due_today: true,
  due_soon: true,
  assigned: true,
  mention: true,
  recurrence: true,
};

interface UiState {
  sidebarCollapsed: boolean;
  selectedTaskId: string | null;
  /** Filtre multi-jours du mini-calendrier (clés YYYY-MM-DD). */
  dayFilter: string[];
  /** Filtre listes (ids) actif dans la sidebar. */
  listFilter: string[];
  prefs: Prefs;
  /** Vues sauvegardées (front-only v1 — TODO endpoint TM config utilisateur). */
  savedViews: SavedView[];
  activeViewId: string;
  toggleSidebar: () => void;
  selectTask: (id: string | null) => void;
  setDayFilter: (days: string[]) => void;
  setListFilter: (ids: string[]) => void;
  setPrefs: (patch: Partial<Prefs>) => void;
  addView: (view: SavedView) => void;
  updateView: (id: string, patch: Partial<SavedView>) => void;
  removeView: (id: string) => void;
  setActiveView: (id: string) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      selectedTaskId: null,
      dayFilter: [],
      listFilter: [],
      prefs: {
        defaultListId: null,
        defaultVisibility: "private",
        defaultPriority: "medium",
        theme: "light",
        lang: "fr",
        weekStart: "mon",
        notifs: DEFAULT_NOTIFS,
      },
      savedViews: seedViews(),
      activeViewId: "seed-all",
      toggleSidebar: () =>
        set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      selectTask: (id) => set({ selectedTaskId: id }),
      setDayFilter: (days) => set({ dayFilter: days }),
      setListFilter: (ids) => set({ listFilter: ids }),
      setPrefs: (patch) => set((s) => ({ prefs: { ...s.prefs, ...patch } })),
      addView: (view) =>
        set((s) => ({
          savedViews: [...s.savedViews, view],
          activeViewId: view.id,
        })),
      updateView: (id, patch) =>
        set((s) => ({
          savedViews: s.savedViews.map((v) =>
            v.id === id ? { ...v, ...patch } : v,
          ),
        })),
      removeView: (id) =>
        set((s) => {
          const savedViews = s.savedViews.filter((v) => v.id !== id);
          return {
            savedViews: savedViews.length ? savedViews : seedViews(),
            activeViewId:
              s.activeViewId === id
                ? (savedViews[0]?.id ?? "seed-all")
                : s.activeViewId,
          };
        }),
      setActiveView: (id) => set({ activeViewId: id }),
    }),
    {
      name: "todo.ui",
      version: 3,
      migrate: (persisted) => {
        const s = persisted as UiState;
        if (s?.prefs && !s.prefs.notifs) s.prefs.notifs = DEFAULT_NOTIFS;
        return s;
      },
      partialize: (s) => ({
        prefs: s.prefs,
        sidebarCollapsed: s.sidebarCollapsed,
        savedViews: s.savedViews,
        activeViewId: s.activeViewId,
      }),
    },
  ),
);
